
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @copyright Copyright (C) 2023, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @version 1.0.4
 * @changelog
 *  2023-01-18 (1.0.0) (jhoward)
 *    - <show-button-on-sale-countdown> handles showing how long until a show goes on sale.
 *
 *  2023-01-24 (1.0.1) (jhoward)
 *    - <show-button-on-sale-countdown> now re-calculates every second in the last two minutes of the countdown.
 *    - <show-button-on-sale-countdown> now displays a timezone when hovering over the time a ticket goes on sale.
 *
 *  2023-03-28 (1.0.2) (jhoward)
 *    - <show-button-on-sale-countdown> now shows the date of the week for on sale date.
 *
 *  2023-05-15 (1.0.3) (jhoward)
 *    - <show-button-on-sale-countdown> now shows the time of the show going on sale in MT.
 *
 *  2023-05-15 (1.0.4) (jhoward)
 *    - Fix typos in <show-button-on-sale-countdown>.
 */

import {defineComponent, PropType} from "vue";

export default defineComponent({
  name: "ShowButtonOnSaleCountdown",
  emits: ['on-sale-now'],
  props: {
    onSaleDate: {
      type: Object as PropType<Date>
    },
  },
  mounted() {
    window.setTimeout(this.runTimer, 1000);
  },
  data() {
    return {
      language: {
        en: {
          days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          daysMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          today: 'Today',
          clear: 'Clear',
          dateFormat: 'mm/dd/yyyy',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }
      },

      updateTimer: -1,
      isUpdatePerSecond: false,
      renderCounter: 0,
      
      isLoading: false,
    };
  },
  computed: {
    display(): string {
      //render each increment
      this.renderCounter;

      const days = Math.floor(this.daysBetween(new Date(), this.onSaleDate));
      if (days > 7) {
        return this.language.en.days[this.onSaleDate.getDay()] + ', ' +this.language.en.months[this.onSaleDate.getMonth()] + ' '
            + this.onSaleDate.getDate() + ', '
            + this.onSaleDate.getFullYear()
            + ' @ ' + this.getOnSaleTimeOfDay() + ' MT</span>';
      } else if (days > 1) {
        return ' in ' + days + ' days @ ' + this.getOnSaleTimeOfDay() + ' MT!';
      } else {
        const hours = Math.floor(this.hoursBetween(new Date(), this.onSaleDate));
        if (hours > 1) {
          return ' in ' + hours + ' hours @ ' + this.getOnSaleTimeOfDay() + ' MT!';
        } else {
          const minutes = Math.floor(this.minutesBetween(new Date(), this.onSaleDate));
          if (minutes > 1) {
            if (minutes < 3) {
              this.isUpdatePerSecond = true;
            }
            return ' in ' + minutes + ' minutes @ ' + this.getOnSaleTimeOfDay() + ' MT!';
          } else if ((new Date()).getTime() > this.onSaleDate.getTime()) {
            this.$emit('on-sale-now');
            return '... updating ... ';
          } else {
            this.isUpdatePerSecond = true;
            return ' in ' + Math.floor(this.secondsBetween(new Date(), this.onSaleDate)) + ' seconds!';
          }
        }
      }
    },
  },
  methods: {
    getDateTimezone(date: Date): string {
      const matches = /\(([^(]+)\)/.exec(date.toTimeString())
      if (matches) {
        return matches[1];
      }

      return '';
    },
    getOnSaleTimeOfDay() {
      const timeString = this.onSaleDate.toLocaleTimeString([], {timeZone: 'America/Denver'});
      const matches = /^(\d+):(\d+)(:\d+)? ([AP]M)/.exec(timeString);
      if (matches) {
        return parseInt(matches[1], 10) + ':' + matches[2] + ' ' + matches[4];
      } else {
        return timeString;
      }
    },

    runTimer() {
      clearTimeout(this.updateTimer);
      const timeout = (this.isUpdatePerSecond) ? 1000 : 60000;

      this.renderCounter++;

      window.setTimeout(this.runTimer, timeout);
    },


    daysBetween(startDate: Date, endDate: Date): number {
      let millisecondsPerDay = 24 * 60 * 60 * 1000;

      return (this.treatAsUTC(endDate).getTime() - this.treatAsUTC(startDate).getTime()) / millisecondsPerDay;
    },
    hoursBetween(startDate: Date, endDate: Date): number {
      let millisecondsPerHour = 60 * 60 * 1000;

      return (this.treatAsUTC(endDate).getTime() - this.treatAsUTC(startDate).getTime()) / millisecondsPerHour;
    },
    minutesBetween(startDate: Date, endDate: Date): number {
      let millisecondsPerMinute = 60 * 1000;

      return (this.treatAsUTC(endDate).getTime() - this.treatAsUTC(startDate).getTime()) / millisecondsPerMinute;
    },
    secondsBetween(startDate: Date, endDate: Date): number {
      let millisecondsPerSecond = 1000;

      return (this.treatAsUTC(endDate).getTime() - this.treatAsUTC(startDate).getTime()) / millisecondsPerSecond;
    },
    treatAsUTC(date: Date): Date {
      let result = new Date(date);

      result.setMinutes(result.getMinutes() - result.getTimezoneOffset());

      return result;
    },
  }
})
